// File: frontend/src/views/public/ProductPage.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ArrowRight, Check, AlertTriangle, Sparkles } from 'lucide-react';
import { getProductBySlug, INSURANCE_PRODUCTS } from '../../config/insuranceProducts';
import ScrollAnimation from '../../components/common/ScrollAnimation';
import PageContainer from '../../components/layout/PageContainer';
import AuthModal from '../../components/auth/AuthModal';
import styles from './ProductPage.module.css';
import SEO from '../../components/utility/SEO';

const ProductPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const product = getProductBySlug(productSlug);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isHeroImageLoaded, setIsHeroImageLoaded] = useState(false);
  const [isFeatureImageLoaded, setIsFeatureImageLoaded] = useState(false);

  useEffect(() => {
    if (!product) {
      navigate('/404');
      return;
    }
  
    // Preload images
    const heroImg = new Image();
    heroImg.src = product.heroImage;
    heroImg.onload = () => setIsHeroImageLoaded(true);

    const featureImg = new Image();
    featureImg.src = product.featureImage;
    featureImg.onload = () => setIsFeatureImageLoaded(true);
  }, [product, navigate]);

  if (!product) return null;

  const IconComponent = product.icon;

  // Get other products (exclude current one)
  const otherProducts = Object.values(INSURANCE_PRODUCTS).filter(
    p => p.id !== product.id
  );

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard/upload');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleProductClick = (productSlug) => {
    navigate(`/products/${productSlug}`);
  };

  return (
    <>
      <SEO 
        title={product.name}
        description={product.seoDescription}
        canonical={`${process.env.REACT_APP_APP_URL}/products/${product.slug}`}
        image={product.heroImage}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Hero Section */}
          <section className={styles.hero}>
            <div className={styles.heroGrid}>
              <ScrollAnimation>
                <div className={styles.heroContent}>
                  <div className={styles.iconBadge}>
                    <IconComponent size={32} strokeWidth={2} />
                  </div>
                  <h1 className={styles.heroTitle}>{product.heroTitle}</h1>
                  <p className={styles.heroSubtitle}>{product.heroSubtitle}</p>
                  <button className={styles.heroCTA} onClick={handleCTA}>
                    {isAuthenticated ? 'Upload Policy' : 'Get Started Free'}
                    <ArrowRight size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={`${styles.heroImageContainer} ${isHeroImageLoaded ? styles.loaded : ''}`}>
                  <img
                    src={product.heroImage}
                    alt={product.name}
                    className={styles.heroImage}
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* What It Covers Section */}
          <section className={styles.section}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={`${styles.imageContainer} ${isFeatureImageLoaded ? styles.loaded : ''}`}>
                  <img
                    src={product.featureImage}
                    alt={`${product.name} coverage`}
                    className={styles.image}
                    loading="lazy"
                  />
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.content}>
                  <h2 className={styles.sectionTitle}>
                    What Does {product.name} Cover?
                  </h2>
                  <p className={styles.sectionDescription}>
                    {product.name} typically includes the following coverages. Your specific policy may vary.
                  </p>
                  <ul className={styles.coverageList}>
                    {product.whatItCovers.map((item, index) => (
                      <li key={index}>
                        <Check size={20} strokeWidth={2.5} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* Common Gaps Section */}
          <section className={styles.gapsSection}>
            <div className={styles.gapsContainer}>
              <ScrollAnimation>
                <div className={styles.gapsHeader}>
                  <div className={styles.gapsIconBadge}>
                    <AlertTriangle size={28} strokeWidth={2} />
                  </div>
                  <h2 className={styles.sectionTitle}>
                    Common Coverage Gaps
                  </h2>
                  <p className={styles.sectionDescription}>
                    Most {product.name.toLowerCase()} policies have these gaps. Upload your policy to see if you're affected.
                  </p>
                </div>
              </ScrollAnimation>

              <div className={styles.gapsGrid}>
                {product.commonGaps.map((gap, index) => (
                  <ScrollAnimation key={index} delay={index * 80}>
                    <div className={styles.gapCard}>
                      <div className={styles.gapIcon}>
                        <AlertTriangle size={18} strokeWidth={2.5} />
                      </div>
                      <p className={styles.gapText}>{gap}</p>
                    </div>
                  </ScrollAnimation>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className={styles.section}>
            <div className={styles.featuresContainer}>
              <ScrollAnimation>
                <div className={styles.featuresHeader}>
                  <div className={styles.featuresIconBadge}>
                    <Sparkles size={28} strokeWidth={2} />
                  </div>
                  <h2 className={styles.sectionTitle}>
                    How Allegory Helps with {product.name}
                  </h2>
                  <p className={styles.sectionDescription}>
                    Upload your policy and get instant insights, gap detection, and claims guidance.
                  </p>
                </div>
              </ScrollAnimation>

              <div className={styles.featuresGrid}>
                {product.features.map((feature, index) => (
                  <ScrollAnimation key={index} delay={index * 80}>
                    <div className={styles.featureCard}>
                      <h3 className={styles.featureTitle}>{feature.title}</h3>
                      <p className={styles.featureDescription}>{feature.description}</p>
                    </div>
                  </ScrollAnimation>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className={styles.ctaSection}>
            <div className={styles.ctaBg} />
            <ScrollAnimation>
              <div className={styles.ctaContent}>
                <h2 className={styles.ctaTitle}>
                  Ready to Understand Your {product.name}?
                </h2>
                <p className={styles.ctaSubtitle}>
                  Upload your policy in 60 seconds and get instant insights.
                </p>
                <button className={styles.ctaButton} onClick={handleCTA}>
                  {isAuthenticated ? 'Upload Policy Now' : 'Get Started Free'}
                  <ArrowRight size={20} strokeWidth={2.5} />
                </button>
              </div>
            </ScrollAnimation>
          </section>

          {/* Other Products Carousel */}
          <section className={styles.otherProductsSection}>
            <div className={styles.otherProductsContainer}>
              <ScrollAnimation>
                <div className={styles.otherProductsHeader}>
                  <h2 className={styles.sectionTitle}>
                    Explore Other Insurance Products
                  </h2>
                  <p className={styles.sectionDescription}>
                    We support all major insurance types. Discover how Allegory can help with your other policies.
                  </p>
                </div>
              </ScrollAnimation>

              <div className={styles.productsCarousel}>
                <div className={styles.productsGrid}>
                  {otherProducts.map((otherProduct, index) => {
                    const OtherIcon = otherProduct.icon;
                    return (
                      <ScrollAnimation key={otherProduct.id} delay={index * 60}>
                        <div
                          className={styles.productCard}
                          onClick={() => handleProductClick(otherProduct.slug)}
                        >
                          <div className={styles.productCardIcon}>
                            <OtherIcon size={28} strokeWidth={2} />
                          </div>
                          <h3 className={styles.productCardTitle}>
                            {otherProduct.name}
                          </h3>
                          <p className={styles.productCardDescription}>
                            {otherProduct.heroSubtitle}
                          </p>
                          <div className={styles.productCardLink}>
                            Learn More
                            <ArrowRight size={16} strokeWidth={2.5} />
                          </div>
                        </div>
                      </ScrollAnimation>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialType="sign_up"
        />
      </PageContainer>
    </>
  );
};

export default ProductPage;